import { Box, Grid, List, TextField } from "@mui/material";
import * as React from "react";
import { Permission } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as documentService from "../../../models/services/documentService";
import { ISessionState } from "../../../models/session/sessionState";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { IDocumentListItem } from "source/models/document/documentListItem";
import { FolderTreeNode, IFolderTreeNode } from "source/models/document/folderTreeNode";
import { Dropdown } from "ui/Scripts/source/components/framework/Dropdown/Dropdown/Dropdown";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { CompanyItem } from "source/components/company/Company/CompanyItem";
import { SelectUserDialog } from "source/components/user/SelectUserDialog/SelectUserDialog";
import { UserListItems } from "source/models/user/userListItems";
import { IUserListItem } from "source/models/user/userListItem";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { AvatarName } from "source/components/framework/User/AvatarName";
import { DocumentTreeFolderNode } from "../DocumentFolders/DocumentFolderTreeNode";
import { ConfirmationButton } from "source/components/framework/Button/ConfirmationButton";
import { IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

export interface IDocumentCopyMoveDialogProp {
    sessionState: ISessionState;
    companies: ICompanyMenuItem[];
    companyId: string;
    folderId: string;
    folderPermission: Permission;
    folderTree: IFolderTreeNode;
    items: IDocumentListItem[];
    onOk: (refreshDocuments: boolean, newId: string) => void;
    onCancel: () => void;
}

export interface IDocumentCopyMoveDialogState {
    sourceFolderTreeNode: IFolderTreeNode;
    sourceFullPath: string;
    targetCompanyId: string;
    targetFolderTreeNodes: IFolderTreeNode[];
    targetFolderTreeNode: IFolderTreeNode;
    targetFullPath: string;
    selectedCompany: ICompanyMenuItem;
    resetDocumentVersion: boolean;
    selectedUser: IModifyUser;
    showSelectUserDialog: boolean;
}

export class DocumentCopyMoveDialog extends React.Component<IDocumentCopyMoveDialogProp, IDocumentCopyMoveDialogState> {
    constructor(props: IDocumentCopyMoveDialogProp) {
        super(props);
        const folderTree = new FolderTreeNode(props.folderTree);
        folderTree.removeReadOnlyBranches();
        const targetFolderTreeNodes = folderTree.getAsList([]);
        const targetFolderTreeNode = targetFolderTreeNodes[0];
        const sourceFolderTree = new FolderTreeNode(props.folderTree);
        const sourceFolderTreeNodes = sourceFolderTree.getAsList([]);
        const sourceFolderTreeNode = sourceFolderTreeNodes.find(i => i.id === props.folderId);
        this.state = {
            sourceFolderTreeNode,
            sourceFullPath: sourceFolderTreeNode.getFullPath() + (props.items.length === 1
                ? "\\" + props.items[0].code
                : (props.items.length > 1
                    ? " (" + props.items.length.toString(10) + " " + Translations.DocumentsSelected + ")"
                    : "")),
            targetCompanyId: props.companyId,
            targetFolderTreeNodes: folderTree.getAsList([]),
            targetFolderTreeNode,
            targetFullPath: targetFolderTreeNode.getFullPath(),
            selectedCompany: props.companies.find(i => i.id === props.companyId),
            resetDocumentVersion: false,
            selectedUser: null,
            showSelectUserDialog: false
        };
    }

    handleChangeTargetCompany = async (targetCompanyId: string) => {
        const props = this.props;
        let targetFolderTreeNodes: IFolderTreeNode[];
        if (targetCompanyId === props.companyId) {
            const folderTree = new FolderTreeNode(props.folderTree);
            folderTree.removeReadOnlyBranches();
            targetFolderTreeNodes = folderTree.getAsList([]);
        } else {
            const result = await AppUtils.callService(() => documentService.getTargetCompanyDocumentFolders(targetCompanyId), false);
            if (!result) return;
            result.folderTree.removeReadOnlyBranches();
            targetFolderTreeNodes = result.folderTree.getAsList([]);
        }
        const targetFolderTreeNode = targetFolderTreeNodes[0];
        this.setState({
            targetCompanyId,
            targetFolderTreeNodes,
            targetFolderTreeNode,
            targetFullPath: targetFolderTreeNode.getFullPath(),
            selectedCompany: props.companies.find(i => i.id === targetCompanyId),
            resetDocumentVersion: false,
            selectedUser: null
        });
    };

    handleTargetFolderClick = (aNode: IFolderTreeNode) => {
        if (!aNode?.id) return;
        const targetFolderTreeNodes = this.state.targetFolderTreeNodes.slice(0);
        const targetFolderTreeNode = targetFolderTreeNodes.find(i => i.id === aNode.id);
        if (!targetFolderTreeNode) return;
        if (targetFolderTreeNode.parent) {
            targetFolderTreeNode.expanded = !targetFolderTreeNode.expanded;
        }
        this.setState({
            targetFolderTreeNodes,
            targetFolderTreeNode,
            targetFullPath: targetFolderTreeNode.getFullPath(),
        });
    };

    // #region RenewPersonalization
    handleChangeRenewPersonalizationWithUser = (value: boolean) => {
        if (!value) {
            this.setState({ selectedUser: null });
            return;
        }
        this.setState({
            showSelectUserDialog: true
        });
    };

    handleGetUserSelectListItems = (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, ignoreQrCodeUsers: boolean, requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        return documentService.getTargetCompanyUserListItems(companyId, pageSize, page, filter, sortColumn, sortOrderIsAsc);
    };

    onSelectUserDialogOk = async (users: IUserListItem[]) => {
        if (!users || users.length < 1) return;
        const selectedUser = new ModifyUser();
        selectedUser.code = users[0].code;
        selectedUser.name = users[0].getName();
        selectedUser.picture = users[0].picture;
        this.setState({
            selectedUser,
            showSelectUserDialog: false
        });
    };

    onSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };
    // #endregion RenewPersonalization

    getSaveDataFromState = async (props: IDocumentCopyMoveDialogProp, state: IDocumentCopyMoveDialogState, move: boolean): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("sourceCompanyId", props.companyId);
        result.append("sourceFolderId", props.folderId);
        result.append("sourceDocuments", props.items.map(i => {
            return IdTitle.createIdTitle(i.id, i.code);
        }));
        result.append("targetCompanyId", state.targetCompanyId);
        result.append("targetFolderId", state.targetFolderTreeNode.id);
        result.append("resetDocumentVersion", state.resetDocumentVersion);
        result.append("newUserCode", state.selectedUser?.code ?? "");
        result.append("move", move);
        return result;
    };
    
    saveToDb = async (move: boolean): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        const saveData = await this.getSaveDataFromState(props, state, move);
        //Call server
        if (props.items.length > 0) {
            return !!(await AppUtils.callService(() => documentService.copyDocuments(saveData.obj)));
        } else {
            return !!(await AppUtils.callService(() => documentService.copyFolder(saveData.obj)));
        }
    };

    handleCopy = async () => {
        if (!await this.saveToDb(false)) return;
        const props = this.props;
        const isDocumentCopy = props.items.length > 0;
        props.onOk(
            isDocumentCopy
                ? props.folderId === this.state.targetFolderTreeNode?.id
                : true,
            props.companyId === this.state.targetCompanyId
                ? this.state.targetFolderTreeNode?.id
                : props.folderId);
    };

    handleMove = async () => {
        if (!await this.saveToDb(true)) return;
        const props = this.props;
        props.onOk(
            true,
            props.companyId === this.state.targetCompanyId
                ? this.state.targetFolderTreeNode?.id
                : props.folderId
        );
    };

    handleCancel = async () => {
        this.props.onCancel();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const copyFolders = props.items.length < 1;
        const moveDisabled = state.targetFolderTreeNode.permission < Permission.Edit || props.folderId === state.targetFolderTreeNode?.id;
        return (
            <>
                <Dialog
                    id="documentCopyDialog"
                    maxWidth="lg"
                    title={copyFolders
                        ? Translations.CopyOrMoveFolder
                        : Translations.CopyOrMoveFilesDocuments}
                    body={
                        <Box display="flex" flexDirection="column">
                            <GridRow>
                                <Grid item sm={6}>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Source} fullWidth margin={0}>
                                            <TextField name="code" title={state.sourceFullPath} value={state.sourceFullPath} disabled={true} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                    {props.companyId === state.targetCompanyId &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title="" fullWidth>
                                                    <FormCheckBox
                                                        title={Translations.ResetDocumentVersion}
                                                        checked={state.resetDocumentVersion}
                                                        onChange={(checked: boolean) => { this.setState({ resetDocumentVersion: checked }); }}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                    {props.companyId !== state.targetCompanyId && (props.sessionState.isSuperAdmin || props.sessionState.companyId === state.targetCompanyId) &&
                                        <>
                                            <GridRow>
                                                <Grid item sm={12}>
                                                    <FormField title="" fullWidth>
                                                        <FormCheckBox
                                                            title={Translations.RenewPersonalizationWithUser}
                                                            checked={!!state.selectedUser}
                                                            onChange={this.handleChangeRenewPersonalizationWithUser}
                                                        />
                                                    </FormField>
                                                </Grid>
                                            </GridRow>
                                            {!!state.selectedUser &&
                                                <GridRow>
                                                    <Grid item sm={12}>
                                                        <FormField title="" fullWidth>
                                                            <AvatarName
                                                                code={state.selectedUser.code}
                                                                name={state.selectedUser.name}
                                                                picture={state.selectedUser.picture}
                                                            />
                                                        </FormField>
                                                    </Grid>
                                                </GridRow>
                                            }
                                        </>
                                    }
                                </Grid>
                                <Grid item sm={6}>
                                    {props.companies.length > 1 &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.TargetCompany} fullWidth margin={0}>
                                                    <Dropdown
                                                        autoHeight={true}
                                                        selectedTitle={state.selectedCompany?.name}
                                                        startIcon={
                                                            <AvatarImage
                                                                size={24}
                                                                name={state.selectedCompany?.name}
                                                                image={state.selectedCompany?.logo}
                                                                tooltip={state.selectedCompany?.name}
                                                                marginRight={".5rem"}
                                                            />
                                                        }
                                                        selectMode={true}
                                                        actions={props.companies.map((company) => {
                                                            return {
                                                                title: <CompanyItem company={company} />,
                                                                textTitle: company.name,
                                                                selected: state.targetCompanyId === company.id,
                                                                disabled: !company.active,
                                                                onClick: () => this.handleChangeTargetCompany(company.id)
                                                            };
                                                        })}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.Target} fullWidth margin={props.companies.length > 1 ? null : 0}>
                                                <TextField name="code" title={state.targetFullPath} value={state.targetFullPath} disabled={true} size="small" fullWidth />
                                                <div style={{ maxHeight: "calc(100vh - 420px", overflowY: "auto", overflowX: "hidden" }}>
                                                    <List>
                                                        {state.targetFolderTreeNodes.map((node, index) =>
                                                            <DocumentTreeFolderNode
                                                                key={node.id}
                                                                sessionState={props.sessionState}
                                                                companyId={props.companyId}
                                                                editMode={false}
                                                                node={node}
                                                                selectedId={state.targetFolderTreeNode?.id}
                                                                isFirstNode={index < 1}
                                                                onClick={this.handleTargetFolderClick}
                                                            />
                                                        )}
                                                    </List>
                                                </div>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                </Grid>
                            </GridRow>
                        </Box>
                    }
                    actions={
                        <Box display="flex" justifyContent="end" width="100%">
                            <AppButton variant="outlined" onClick={props.onCancel} sx={{ marginRight: ".5rem" }}>{Translations.Close}</AppButton>
                            {props.folderPermission > Permission.Read &&
                                <ConfirmationButton onClick={this.handleMove} sx={{ marginRight: ".5rem" }} disabled={moveDisabled}>{Translations.Move}</ConfirmationButton>
                            }
                            <ConfirmationButton onClick={this.handleCopy} disabled={state.targetFolderTreeNode.permission < Permission.Edit}>{Translations.Copy}</ConfirmationButton>
                        </Box>
                    }
                    onClose={this.handleCancel}
                />
                {state.showSelectUserDialog &&
                    <SelectUserDialog
                        companyId={state.targetCompanyId}
                        onOk={this.onSelectUserDialogOk}
                        onCancel={this.onSelectUserDialogCancel}
                        onGetUserSelectListItems={this.handleGetUserSelectListItems}
                    />
                }
            </>
        );
    }
}